import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { ip_back } from "../../config";
import { canAccessExerciseProposals } from "../../utils/exerciseProposalsAccess";
import { CoachObjectiveMetricPicker } from "../../components/CoachObjectiveMetricPicker";
import { currentAppLanguage } from "../../utils/appLanguages";
import {
  localizedDraftName,
  localizedDraftObjective,
  localizedExerciseName,
  localizedSubTypeName,
  localizedTypeName,
} from "../../utils/localizedCatalogFields";

export default function ExerciseProposals() {
  const { t, i18n } = useTranslation();
  const info = JSON.parse(localStorage.getItem("user") || "{}");
  const allowed = canAccessExerciseProposals(info);
  const lang = currentAppLanguage(i18n);

  const [proposals, setProposals] = useState([]);
  const [lookups, setLookups] = useState({
    types: [],
    subTypes: [],
    competencyBlocks: [],
    exercises: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [approveDialog, setApproveDialog] = useState(null);
  const [approveAction, setApproveAction] = useState("create_new");
  const [typeId, setTypeId] = useState("");
  const [subTypeId, setSubTypeId] = useState("");
  const [mapExId, setMapExId] = useState("");
  const [parentExId, setParentExId] = useState("");
  const [coachNote, setCoachNote] = useState("");
  const [metricKinds, setMetricKinds] = useState([]);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  const base = () => `${String(ip_back || "").replace(/\/?$/, "/")}`;

  const typeLabel = useCallback(
    (row) => localizedTypeName(row, lang) || row?.type_id,
    [lang]
  );
  const subLabel = useCallback(
    (row) => localizedSubTypeName(row, lang) || row?.sub_type_id,
    [lang]
  );

  const loadProposals = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    setError(null);
    try {
      const clubId = info.club_id || "";
      const res = await fetch(
        `${base()}api/admin/exercise-proposals?status=pending${clubId ? `&clubId=${encodeURIComponent(clubId)}` : ""}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to load proposals");
      setProposals(data.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [allowed, info.club_id]);

  const loadLookups = useCallback(async () => {
    try {
      const clubId = info.club_id || "";
      const res = await fetch(
        `${base()}api/admin/exercise-proposals/lookups${clubId ? `?clubId=${encodeURIComponent(clubId)}` : ""}`,
        { headers: authHeaders() }
      );
      const data = await res.json();
      if (data.success) setLookups(data.data || lookups);
    } catch (_) {
      /* ignore */
    }
  }, [info.club_id]);

  useEffect(() => {
    loadProposals();
    loadLookups();
  }, [loadProposals, loadLookups]);

  const filteredSubTypes = useMemo(() => {
    if (!typeId) return lookups.subTypes || [];
    return (lookups.subTypes || []).filter((s) => !s.type_id || s.type_id === typeId);
  }, [lookups.subTypes, typeId]);

  const approveMetricExId = useMemo(() => {
    if (!approveDialog) return null;
    return (
      mapExId ||
      approveDialog.selected_ex_id ||
      approveDialog.parent_ex_id ||
      approveDialog.base_ex_id ||
      null
    );
  }, [approveDialog, mapExId]);

  if (!allowed) {
    return <Navigate to="/Dashboard" replace />;
  }

  const openApprove = (proposal) => {
    setApproveDialog(proposal);
    setApproveAction(proposal.parent_ex_id ? "create_variation" : "create_new");
    setTypeId(proposal.suggested_type_id || "");
    setSubTypeId(proposal.suggested_sub_type_id || "");
    setParentExId(proposal.parent_ex_id || proposal.base_ex_id || "");
    setMapExId("");
    setCoachNote("");
    setMetricKinds([]);
  };

  const handleApprove = async () => {
    if (!approveDialog) return;
    const body = {
      action: approveAction,
      typeId: typeId || undefined,
      subTypeId: subTypeId || undefined,
      parentExId: parentExId || undefined,
      mapExId: mapExId || undefined,
      coachNote,
      goal_role: approveDialog.payload?.goal_role || "primary",
      ...(metricKinds.length ? { metric_kinds: metricKinds } : {}),
    };
    try {
      const res = await fetch(
        `${base()}api/admin/exercise-proposals/${encodeURIComponent(approveDialog.proposal_id)}/approve`,
        { method: "POST", headers: authHeaders(), body: JSON.stringify(body) }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Approve failed");
      setSnack({
        open: true,
        message: t("exerciseProposalApproved") || "Exercise approved into club catalog",
        severity: "success",
      });
      setApproveDialog(null);
      loadProposals();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };

  const handleReject = async (proposal) => {
    try {
      const res = await fetch(
        `${base()}api/admin/exercise-proposals/${encodeURIComponent(proposal.proposal_id)}/reject`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ coachNote: "Rejected" }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Reject failed");
      setSnack({
        open: true,
        message: t("exerciseProposalRejected") || "Proposal rejected",
        severity: "info",
      });
      loadProposals();
    } catch (e) {
      setSnack({ open: true, message: e.message, severity: "error" });
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t("exerciseProposalsTitle") || "Exercise proposals"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("exerciseProposalsHint") ||
            "Review personalized exercises from Digiping and session personalize. Approve as club catalog entries or variations."}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Typography>{t("loading") || "Loading…"}</Typography>
        ) : proposals.length === 0 ? (
          <Alert severity="info">{t("noPendingExerciseProposals") || "No pending exercise proposals."}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("player") || "Player"}</TableCell>
                <TableCell>{t("exercise") || "Exercise"}</TableCell>
                <TableCell>{t("source") || "Source"}</TableCell>
                <TableCell>{t("level") || "Level"}</TableCell>
                <TableCell>{t("suggestedType") || "Suggested type"}</TableCell>
                <TableCell align="right">{t("actions") || "Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.map((p) => {
                const type = (lookups.types || []).find((x) => x.type_id === p.suggested_type_id);
                return (
                  <TableRow key={p.proposal_id}>
                    <TableCell>
                      <strong>{p.player_name}</strong>
                    </TableCell>
                    <TableCell>
                      <div>{localizedDraftName(p, lang) || p.draft_name_en}</div>
                      {localizedDraftObjective(p, lang) ? (
                        <Typography variant="caption" color="text.secondary">
                          {localizedDraftObjective(p, lang).slice(0, 120)}
                        </Typography>
                      ) : null}
                      {p.parent_ex_id ? (
                        <Chip size="small" label="variation" sx={{ mt: 0.5 }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{p.source_channel}</TableCell>
                    <TableCell>{p.level_band || "—"}</TableCell>
                    <TableCell>{type ? typeLabel(type) : p.suggested_type_id || "—"}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openApprove(p)}>
                        {t("approve") || "Approve"}
                      </Button>
                      <Button size="small" color="inherit" onClick={() => handleReject(p)}>
                        {t("reject") || "Reject"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={!!approveDialog} onClose={() => setApproveDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("approveExerciseProposal") || "Approve exercise proposal"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Typography variant="subtitle2">
            {localizedDraftName(approveDialog, lang) || approveDialog?.draft_name_en}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>{t("action") || "Action"}</InputLabel>
            <Select
              label={t("action") || "Action"}
              value={approveAction}
              onChange={(e) => setApproveAction(e.target.value)}
            >
              <MenuItem value="create_new">{t("createNewCatalog") || "Create new catalog exercise"}</MenuItem>
              <MenuItem value="create_variation">{t("createVariation") || "Create variation of parent"}</MenuItem>
              <MenuItem value="map_existing">{t("mapExisting") || "Map to existing exercise"}</MenuItem>
            </Select>
          </FormControl>

          {approveAction !== "map_existing" ? (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>{t("type") || "Type"}</InputLabel>
                <Select
                  label={t("type") || "Type"}
                  value={typeId}
                  onChange={(e) => {
                    setTypeId(e.target.value);
                    setSubTypeId("");
                  }}
                >
                  {(lookups.types || []).map((ty) => (
                    <MenuItem key={ty.type_id} value={ty.type_id}>
                      {typeLabel(ty)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>{t("subType") || "Sub-type"}</InputLabel>
                <Select
                  label={t("subType") || "Sub-type"}
                  value={subTypeId}
                  onChange={(e) => setSubTypeId(e.target.value)}
                >
                  <MenuItem value="">{t("none") || "None"}</MenuItem>
                  {filteredSubTypes.map((st) => (
                    <MenuItem key={st.sub_type_id} value={st.sub_type_id}>
                      {subLabel(st)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {approveAction === "create_variation" ? (
                <FormControl fullWidth size="small">
                  <InputLabel>{t("parentExercise") || "Parent exercise"}</InputLabel>
                  <Select
                    label={t("parentExercise") || "Parent exercise"}
                    value={parentExId}
                    onChange={(e) => setParentExId(e.target.value)}
                  >
                    {(lookups.exercises || []).slice(0, 200).map((ex) => (
                      <MenuItem key={ex.ex_id} value={ex.ex_id}>
                        {localizedExerciseName(ex, lang) || ex.ex_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </>
          ) : (
            <FormControl fullWidth size="small">
              <InputLabel>{t("existingExercise") || "Existing exercise"}</InputLabel>
              <Select
                label={t("existingExercise") || "Existing exercise"}
                value={mapExId}
                onChange={(e) => setMapExId(e.target.value)}
              >
                {(lookups.exercises || []).slice(0, 200).map((ex) => (
                  <MenuItem key={ex.ex_id} value={ex.ex_id}>
                    {localizedExerciseName(ex, lang) || ex.ex_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {approveDialog?.player_user_id && approveMetricExId ? (
            <CoachObjectiveMetricPicker
              playerUserId={approveDialog.player_user_id}
              exId={approveMetricExId}
              goalRole={approveDialog.payload?.goal_role || "primary"}
              goalLabel={approveDialog.payload?.goal_label || null}
              mode="validate"
              onChange={setMetricKinds}
            />
          ) : null}

          <TextField
            label={t("coachNote") || "Coach note"}
            value={coachNote}
            onChange={(e) => setCoachNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>{t("cancel") || "Cancel"}</Button>
          <Button variant="contained" onClick={handleApprove}>
            {t("confirm") || "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </AdminLayout>
  );
}
